import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { corridor as corridorApi } from "@/lib/corridorApi";

/**
 * CorridorContributeCard — the opt-in tile on Result page that lets a user
 * publish this scan to their state's Corridor map.
 *
 * Design goals:
 *  • Opt-in per scan, default off. Never auto-enroll.
 *  • Explain the privacy model in one sentence, right at the toggle, so the
 *    user makes an informed choice.
 *  • Requires the scan to already carry a `location` (any browser geo grant
 *    from the identify flow). If not, we hide the toggle and show a small
 *    "grant location" affordance the parent wires up.
 *  • Toggle state persists via the /corridor/contribute + DELETE endpoints —
 *    no local caching, so a page reload always reflects reality.
 *
 * A11y note (WCAG 3.2.2 / 2.4.3):
 *  The switch stays enabled at all times once the initial lookup completes.
 *  Deriving `disabled` from the same state the toggle mutates (as an earlier
 *  version did) meant a successful toggle-off destroyed focus. If the user
 *  tries to turn the switch ON without a location, the missing-location
 *  error is surfaced through `aria-describedby` on the toggle and via a
 *  toast — the switch itself remains focusable and reversible.
 */
export default function CorridorContributeCard({ scan, onGrantLocation }) {
  const { t } = useTranslation();
  const [contributed, setContributed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false); // initial lookup done?

  const lat = scan?.location?.lat;
  const lon = scan?.location?.lon ?? scan?.location?.lng; // codebase uses `lng`, API uses `lon`
  const hasLocation = Number.isFinite(lat) && Number.isFinite(lon);

  // On mount, ask the server whether THIS scan is already in the corridor.
  // Cheap — the endpoint returns up to 2000 rows for the user which is
  // vastly more than most users will ever have.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!scan?.scan_id) return;
      try {
        const { data } = await corridorApi.mine();
        if (cancelled) return;
        const mine = (data?.contributions || []).some((c) => c.scan_id === scan.scan_id);
        setContributed(mine);
      } catch {
        // Silent — this is a nice-to-have status. Toggle will still work.
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scan?.scan_id]);

  const toggle = async () => {
    if (!scan?.scan_id || loading) return;
    setLoading(true);
    try {
      if (contributed) {
        await corridorApi.uncontribute(scan.scan_id);
        setContributed(false);
        toast.success(t("corridor.contribute.removed"));
      } else {
        if (!hasLocation) {
          toast.error(t("corridor.contribute.needsLocation"));
          setLoading(false);
          return;
        }
        await corridorApi.contribute(scan.scan_id, lat, lon);
        setContributed(true);
        toast.success(t("corridor.contribute.added"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("corridor.contribute.failed"));
    } finally {
      setLoading(false);
    }
  };

  // The switch is only disabled during in-flight or before initial lookup —
  // NEVER as a consequence of the switch's own state. This keeps focus on
  // the control across toggles (WCAG 3.2.2 On Input, 2.4.3 Focus Order).
  const switchDisabled = loading || !checked;
  const showLocationHint = !hasLocation && !contributed;
  const describedById = showLocationHint ? `corridor-toggle-desc-${scan?.scan_id || "x"}` : undefined;

  return (
    <div
      className="rounded-3xl border border-leaf/20 bg-gradient-to-br from-leaf/[0.04] to-transparent p-4"
      data-testid="corridor-contribute-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-2xl bg-leaf/15 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-leaf" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              {/* h2 (not h3) so headings on /result descend h1 → h2 → h2
                  without gaps (WCAG 1.3.1 / best-practice heading-order). */}
              <h2 className="font-display text-lg text-leaf-ink leading-tight">
                {t("corridor.contribute.title")}
              </h2>
              <p className="mt-1 text-[13px] text-leaf-mist leading-relaxed">
                {t("corridor.contribute.help")}
              </p>
            </div>
            {/* iOS-style toggle. Native <button role=switch> is more
                accessible than a custom checkbox and keeps VoiceOver happy. */}
            <button
              type="button"
              role="switch"
              aria-checked={contributed}
              aria-label={t("corridor.contribute.toggleAria")}
              aria-describedby={describedById}
              onClick={toggle}
              disabled={switchDisabled}
              data-testid="corridor-contribute-toggle"
              className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition ${
                contributed ? "bg-leaf" : "bg-parchment"
              } ${switchDisabled ? "opacity-60" : ""} disabled:cursor-not-allowed`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  contributed ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {showLocationHint && (
            <div className="mt-3">
              <p
                id={describedById}
                className="text-[12px] text-rust mb-2"
              >
                {t("corridor.contribute.locationRequired")}
              </p>
              {onGrantLocation && (
                <button
                  type="button"
                  onClick={onGrantLocation}
                  data-testid="corridor-contribute-grant-loc"
                  className="text-xs rounded-full px-3 py-1.5 bg-leaf text-white hover:bg-leaf-deep transition"
                >
                  {t("corridor.contribute.grantLocation")}
                </button>
              )}
            </div>
          )}

          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-leaf-mist">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-leaf" strokeWidth={1.5} />
            <span>{t("corridor.contribute.privacy")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
